/**
 * Wait out a launch that cannot proceed YET (gh #3745, gh #3751).
 *
 * One Rekor preview base admits ONE eval session at a time, so a second launch
 * against the same fixture is refused with 409 `fixture_target_in_use` rather
 * than being allowed to corrupt the first. Two CI branches pointed at one hub is
 * a normal situation, not an error, so the CLI waits by default instead of
 * failing the job — the queueing is entirely client-side (a re-POST with
 * backoff), so nothing on the server holds a waiter.
 *
 * Rekor's transient backpressure (409 `fixture_seed_unavailable`) waits the same
 * way and for the same reason: the launch is refused by a condition that clears
 * on its own, and the caller already owns a bounded budget to spend on it. The
 * 5xx Rekor answered upstream never reaches this layer as a status — it is
 * carried in the refusal's message text, for the operator to read.
 *
 * The wait lives INSIDE the caller's existing `--timeout` budget: a job that
 * would have failed at its deadline still fails at that deadline, it just spends
 * the interval waiting rather than erroring immediately.
 *
 * This is the ONE budget owner for a retryable launch (gh #3751): the backend
 * never sleeps on a launch. A bounded server-side retry would pin the hub's
 * shared HubDO — the singleton every request for that hub goes through — for the
 * whole drain window, and would buy nothing this loop does not already have
 * (a deadline, an interrupt, and `--no-queue`).
 */

import type { RunSessionError } from '@wayai/core/contracts';
import { extractApiErrorCode, extractApiMessage, extractApiRetryAfterSeconds } from './errors.js';

/**
 * Backoff between re-POSTs. Short to start — the holder may be seconds from done
 * — then capped: each attempt is a full run request, which re-validates the suite
 * and attempts a Rekor acquire, so a long wait should not keep paying that every
 * few seconds.
 */
export const FIXTURE_QUEUE_INITIAL_DELAY_MS = 5_000;
export const FIXTURE_QUEUE_MAX_DELAY_MS = 30_000;

/**
 * The ONLY launch refusals waiting resolves — both clear without anyone acting:
 *   • `fixture_target_in_use`      — another live session holds the fixture and
 *                                    releases it when it finishes.
 *   • `fixture_seed_unavailable`   — Rekor is momentarily out of write capacity
 *                                    (gh #3751). The acquire was rejected
 *                                    PRE-STORAGE, so nothing landed and an
 *                                    identical retry is safe by construction.
 *
 * `fixture_lease_not_owned` (rotated credential) and
 * `fixture_lease_capacity_exceeded` (full ledger) share the 409 but never clear
 * on their own — waiting on them burns the caller's whole budget re-asking a
 * question only an operator can answer. `fixture_seed_failed` is the same story
 * for misconfiguration, which is exactly why the transient case needed its own
 * code rather than being folded into it.
 *
 * Typed `ReadonlySet<string>` but BUILT from `RunSessionError` literals: the
 * construction keeps a typo a compile error, while the lookup takes the raw
 * server string without casting an arbitrary body field into the union.
 */
const QUEUEABLE_LAUNCH_ERRORS: ReadonlySet<string> = new Set<RunSessionError>([
  'fixture_target_in_use',
  'fixture_seed_unavailable',
]);

/**
 * True when the failure is one this loop waits out rather than propagating.
 *
 * Exported so `run-eval` prints its "raise --timeout" hint for precisely the set
 * this loop actually waits on — a second list would drift from this one.
 */
export function isQueueableLaunchError(err: unknown): boolean {
  const code = extractApiErrorCode(err);
  return code !== null && QUEUEABLE_LAUNCH_ERRORS.has(code);
}

export interface FixtureQueueOptions<T> {
  /** Issues the run request. Re-invoked per attempt — never a retried promise. */
  attempt: () => Promise<T>;
  /** Epoch ms after which waiting stops and the last conflict is thrown. */
  deadlineMs: number;
  /** Fail fast on the first conflict instead of waiting (`--no-queue`). */
  noQueue: boolean;
  now: () => number;
  sleep: (ms: number) => Promise<void>;
  /**
   * Called once per conflict, so the caller owns all console output. `message` is
   * the backend's own refusal text, which names the holding session when the
   * caller is entitled to know it. (It is used instead of `details.holder`
   * because `ApiError` masks `session_id`-keyed values — see `mask-secrets.ts`.)
   */
  onWait: (info: { message: string; waitMs: number }) => void;
  /** True once the user has interrupted; stops waiting without another attempt. */
  interrupted?: () => boolean | Promise<boolean>;
}

/**
 * Run `attempt`, waiting out the queueable refusals until it succeeds or the
 * deadline passes. Every other failure — including the other 409s, which waiting
 * cannot resolve — propagates immediately.
 *
 * Each attempt re-POSTs the SAME session id, which is what makes the retry
 * lease-safe: the backend persisted the attempted lease tuple before its
 * outbound call, so the next attempt replays that exact `lease_id` instead of
 * minting a new one. A fresh id against an acquire that did land is the
 * permanent wedge the backend's replay design exists to dissolve.
 */
export async function runWithFixtureQueue<T>(opts: FixtureQueueOptions<T>): Promise<T> {
  let delay = FIXTURE_QUEUE_INITIAL_DELAY_MS;
  for (;;) {
    try {
      return await opts.attempt();
    } catch (err) {
      if (!isQueueableLaunchError(err)) throw err;
      if (opts.noQueue) throw err;

      // Wait only for time we actually have. Sleeping past the deadline would
      // turn a bounded job into one that overruns its own timeout.
      const remaining = opts.deadlineMs - opts.now();
      if (remaining <= 0) throw err;
      // The server's `Retry-After` is a FLOOR, not the schedule: asking again
      // before it elapses is guaranteed to be refused, so respect it, but keep
      // the exponential growth above it so a long outage still backs off. Both
      // are then clamped to the remaining budget — the deadline outranks both.
      const retryAfterMs = (extractApiRetryAfterSeconds(err) ?? 0) * 1000;
      const waitMs = Math.min(Math.max(delay, retryAfterMs), remaining);

      opts.onWait({ message: extractApiMessage(err), waitMs });
      await opts.sleep(waitMs);
      if (await opts.interrupted?.()) throw err;
      // Re-check AFTER sleeping: `waitMs` is clamped to land exactly on the
      // deadline, so without this the loop issues one more attempt at t ==
      // deadline. If the fixture just freed, that request starts a session with
      // zero budget left to watch it — the caller reports a timeout and exits
      // while the session runs on, holding the lease it just took.
      if (opts.now() >= opts.deadlineMs) throw err;
      delay = Math.min(delay * 2, FIXTURE_QUEUE_MAX_DELAY_MS);
    }
  }
}
